import { AfterViewInit, Component, DestroyRef, ElementRef, Input, OnChanges, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { MetisMenu } from 'metismenujs';
import { EventService } from '../../core/services/event.service';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { logo, ROLES } from 'src/app/app-const';
import { AuthenticationService, MgUser } from 'src/app/core/services/auth.service';
import { LanguageService } from 'src/app/core/services/language.service';
import { AcademyService } from 'src/app/core/backend/academy/services';
import { CredentialsModalComponent } from 'src/app/shared/components/credentials-modal/credentials-modal.component';
import { MENU } from './menu';
import { MenuItem } from './menu.model';
import { SimplebarAngularComponent } from 'simplebar-angular';
import { BrandingService } from 'src/app/core/backend/common/services';
import { BrandingGetBrandingColorsLogoResponseData } from 'src/app/core/backend/common/models';
import { AcademyBrandingStateService } from 'src/app/core/services/academy-branding-state.service';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  standalone: false
})
export class SidebarComponent implements OnInit, AfterViewInit, OnChanges {
  @ViewChild('componentRef') scrollRef!: SimplebarAngularComponent;
  @Input() isCondensed = false;
  private metisMenu: MetisMenu | null = null;
  data: any;
  logos = logo;
  academyLogo = logo.light;
  menuItems: MenuItem[] = [];
  private loadedAcademyId = '';

  @ViewChild('sideMenu') sideMenu!: ElementRef;
  acc: MgUser | null = null;

  // Subscription alert state
  showSubscriptionAlert = false;
  subscriptionPlanName: string | null = null;
  subscriptionEndDate: string | null = null;
  subscriptionDaysRemaining: number | null = null;
  subscriptionStatus: string | null = null;
  private readonly NEAR_EXPIRY_DAYS = 7;

  constructor(
    private authService: AuthenticationService,
    private eventService: EventService,
    private router: Router,
    private auth: AuthenticationService,
    public translate: TranslateService,
    public languageService: LanguageService,
    private http: HttpClient,
    private academyService: AcademyService,
    private brandingService: BrandingService,
    private brandingState: AcademyBrandingStateService,
    private destroyRef: DestroyRef
  ) {
    this.authService.identity()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();

    this.brandingState.logo$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((logoUrl) => {
        this.academyLogo = logoUrl || this.logos.light;
      });

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this._activateMenuDropdown();
        this._scrollElement();
      }
    });

    this.auth.currentUserSubject
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.acc = res;
          this.refreshMenu(res);
          this.loadSubscriptionAlert();
          this.syncAcademyMenuContext(res);
          this.loadAcademyLogo(res);
        }
      });
  }


  isBranchMode = false;
  isMasterPortalUser = false;
  showBackToAcademies = false;

  exitBranchAction() {
    this.authService.exitBranch();
  }

  returnToAllAcademiesAction() {
    this.authService.returnToAllAcademies();
  }

  returnToMainAcademyAction() {
    this.authService.returnToMainAcademy();
  }

  private syncAcademyMenuContext(user: MgUser | null): void {
    const originalType = Number((user as any)?.user_type?.id ?? (user as any)?.user_type_id ?? 0);
    this.isMasterPortalUser = originalType === ROLES.MasterAcademy
      || originalType === ROLES.FranchiseManager
      || !!user?.roles?.includes(ROLES.MasterAcademy)
      || !!user?.roles?.includes(ROLES.FranchiseManager);
    this.isBranchMode = !!localStorage.getItem('impersonated_branch_id');
    this.showBackToAcademies = this.isMasterPortalUser
      && !!localStorage.getItem('impersonated_main_academy_id')
      && !this.isBranchMode;
  }

  ngOnInit() {
    this.initialize();
    this._scrollElement();
    this.loadSubscriptionAlert();
  }

  ngAfterViewInit() {
    this.initMetisMenu();
  }

  ngOnChanges() {
    if (this.isCondensed) {
      this.destroyMetisMenu();
    } else {
      setTimeout(() => this.initMetisMenu());
    }
  }

  private initMetisMenu() {
    if (this.sideMenu && !this.metisMenu) {
      this.metisMenu = new MetisMenu(this.sideMenu.nativeElement, {
        toggle: true, // This ensures only one dropdown is open at a time
        triggerElement: '.has-arrow, .has-dropdown, is-parent', // Elements that trigger the dropdown
        parentTrigger: 'li', // Parent element of the trigger
        subMenu: 'ul' // The submenu element
      });
      this._activateMenuDropdown();
    }
  }

  private destroyMetisMenu() {
    if (this.metisMenu) {
      this.metisMenu.dispose();
      this.metisMenu = null;
    }
  }

  clickElWhenCollapsed() {
    if (document.body.classList.contains('vertical-collpsed')) {
      this._activateMenuDropdown();
    }
  }

  _scrollElement() {
    setTimeout(() => {
      const activeElements = document.getElementsByClassName("mm-active");
      if (activeElements.length > 0 && this.scrollRef?.SimpleBar) {
        const currentPosition = (activeElements[0] as HTMLElement).offsetTop;
        if (currentPosition > 500) {
          this.scrollRef.SimpleBar.getScrollElement().scrollTop = currentPosition + 300;
        }
      }
    }, 300);
  }

  _removeAllClass(className: string) {
    const els = document.getElementsByClassName(className);
    while (els[0]) {
      els[0].classList.remove(className);
    }
  }

  _activateMenuDropdown() {
    // Clear existing active states
    this._removeAllClass('active');
    this._removeAllClass('mm-active');
    this._removeAllClass('mm-show');

    const currentPath = this.router.url.split('?')[0]; // Get path without query params
    const links = Array.from(document.getElementsByClassName('side-nav-link-ref')) as HTMLAnchorElement[];

    // Prefer an exact route before prefix matches. Without this, /crm-leads is found before
    // /crm-leads/performance-indicators and the item above the report is highlighted.
    let activeLink = links.find((link) => link.pathname === currentPath);
    if (!activeLink) {
      activeLink = links
        .filter((link) => link.pathname !== '/' && currentPath.startsWith(link.pathname + '/'))
        .sort((left, right) => right.pathname.length - left.pathname.length)[0];
    }

    // If no exact match, try to find parent path
    if (!activeLink) {
      const pathParts = currentPath.split('/').filter(Boolean);
      for (let i = pathParts.length - 1; i > 0; i--) {
        const testPath = '/' + pathParts.slice(0, i).join('/');
        activeLink = links.find(link => link.pathname === testPath);
        if (activeLink) break;
      }
    }

    if (activeLink) {
      // Mark the link as active
      activeLink.classList.add('active');

      // Activate parent dropdowns (top-level section headers only — not submenu rows).
      let parent = activeLink.closest('li');
      while (parent) {
        const isSubMenuRow = parent.parentElement?.classList.contains('sub-menu');
        if (!isSubMenuRow) {
          parent.classList.add('mm-active');
        }

        const dropdown = parent.querySelector('.sub-menu');
        if (dropdown) {
          dropdown.classList.add('mm-show');
        }

        const parentMenu = parent.closest('ul');
        parent = parentMenu ? parentMenu.closest('li') : null;
      }

      // Update MetisMenu state
      if (this.metisMenu) {
        this.metisMenu.update();
      }
    }
  }
  initialize(): void {
    this.menuItems = this.filterMenuItems(MENU, this.auth.currentUserValue);
  }

  private refreshMenu(user: MgUser | null): void {
    this.menuItems = this.filterMenuItems(MENU, user);
    setTimeout(() => {
      this.destroyMetisMenu();
      this.initMetisMenu();
    });
  }

  private filterMenuItems(items: MenuItem[], user: MgUser | null): MenuItem[] {
    return items
      // Programs is available as a tab inside Activities, not as a separate
      // sidebar entry. Keep this guard in case an old menu item is cached or
      // returned by another menu source.
      .filter((item) => item.label !== 'MENU.PROGRAMS' && item.link !== '/activities?tab=programs')
      .filter((item) => item.academyFeature !== 'programs_memories'
        || this.auth.hasProgramsMemoriesAccess(user))
      .filter((item) => item.academyFeature !== 'zamalek_only'
        || this.auth.hasZamalekAccess(user))
      .filter((item) => item.academyFeature !== 'crm_leads'
        || this.auth.hasCrmAccess(user))
      // CRM staff assigned to a branch are normalized to BranchManager for
      // layout purposes. Do not let that expose CRM menus to real branch managers.
      .filter((item) => this.canShowCrmMenuItem(item, user))
      .filter((item) => item.academyFeature !== 'payment_links'
        || this.auth.hasPaymentLinksAccess(user))
      .filter((item) => item.academyFeature !== 'chatbot'
        || this.auth.hasChatbotAccess(user))
      // Subscriptions stay on Branch Manager + CRM CS. Main Academy Admin shares
      // user_type with CS, so hide the item when they have settings.view (real admin).
      .filter((item) => this.canShowSubscriptionsMenuItem(item, user))
      .map((item) => ({
        ...item,
        subItems: item.subItems ? this.filterMenuItems(item.subItems, user) : undefined,
      }));
  }

  private canShowCrmMenuItem(item: MenuItem, user: MgUser | null): boolean {
    if (item.academyFeature !== 'crm_leads') {
      return true;
    }

    const isBranchContext = (user?.roles ?? []).includes(ROLES.BranchManager);

    return !isBranchContext || this.auth.isCrmCsEmployee(user);
  }

  /**
   * Branch managers (incl. admin switched into a branch) always keep subscriptions.
   * AcademyAdmin on main academy: only CRM CS (leads.view, no settings.view).
   */
  private canShowSubscriptionsMenuItem(item: MenuItem, user: MgUser | null): boolean {
    if (item.link !== '/subscriptions') {
      return true;
    }

    const roles = user?.roles ?? [];
    if (roles.includes(ROLES.FranchiseManager)) {
      return true;
    }
    if (roles.includes(ROLES.BranchManager)) {
      return true;
    }

    if (!roles.includes(ROLES.AcademyAdmin)) {
      return false;
    }

    // CRM staff share the academy-admin user type. A manager whose saved role
    // also includes settings.view must still keep the subscriptions page.
    if (this.auth.isCrmCsEmployee(user)) {
      return true;
    }

    return this.auth.hasPermission(['leads.view'])
      && !this.auth.hasPermission(['settings.view', 'settings/view']);
  }

  onToggleMobileMenu() {
    this.isCondensed = !this.isCondensed;
    document.body.classList.toggle('sidebar-enable');
    // document.body.classList.toggle('vertical-collpsed');

    // if (window.screen.width <= 768) {
    //   document.body.classList.remove('vertical-collpsed');
    // }
  }

  logout() {
    this.auth.logout();
    this.router.navigate(['/account/login']);
  }

  canManageAcademySubscription(): boolean {
    // Plan upgrade/renew is main-academy admin only. CS staff share AcademyAdmin
    // user_type, so also require settings.view.
    const roles = this.auth.currentUserValue?.roles ?? this.acc?.roles ?? [];
    const isMainAcademyAdmin = roles.some((role) =>
      [ROLES.AcademyAdmin, ROLES.MasterAcademy].includes(role)
    );

    return isMainAcademyAdmin && this.auth.hasPermission(['settings.view', 'settings/view']);
  }

  private loadSubscriptionAlert(): void {
    if (!this.canManageAcademySubscription()) {
      this.showSubscriptionAlert = false;
      return;
    }

    const academyId =
      this.authService.getCurrentMainAcademyContextId()
      || (this.acc?.academy_id ? Number(this.acc.academy_id) : null);

    if (!academyId) return;

    this.academyService.apiSubscriptionAcademyGetAcademyDataGet({ academy_id: String(academyId) })
      .subscribe({
        next: (res: any) => {
          const payload = (res && (res.data ?? res)) || res || {};
          const endDate = payload?.subscription_end_date ?? payload?.subscription?.end_date ?? payload?.end_date ?? payload?.subscription_end;
          const status = payload?.subscription_status ?? payload?.status ?? payload?.subscription?.status ?? null;
          const planName = payload?.subscription?.plan_name ?? payload?.plan_name ?? null;

          this.subscriptionStatus = typeof status === 'string' ? status : null;
          this.subscriptionPlanName = planName;
          this.subscriptionEndDate = endDate;

          if (endDate) {
            const days = Math.ceil((new Date(endDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
            this.subscriptionDaysRemaining = days;
            const isExpired = days < 0;
            const isInactive = typeof this.subscriptionStatus === 'string'
              && this.subscriptionStatus.toLowerCase().includes('inactive');
            const isNearExpiry = !isExpired && days >= 0 && days <= this.NEAR_EXPIRY_DAYS;

            this.showSubscriptionAlert = isExpired || isInactive || isNearExpiry;
          } else {
            const isInactive = typeof this.subscriptionStatus === 'string'
              && this.subscriptionStatus.toLowerCase().includes('inactive');
            this.showSubscriptionAlert = isInactive;
          }
        },
        error: () => {
          // ignore silently
        }
      });
  }

  renewFromSidebar(): void {
    this.router.navigate(['/academy/renew']);
  }

  showUpgradeModal(): void {
    const btnWhite = 'btn btn-white flex-fill h-40px fs-1_125rem fw-medium';
    CredentialsModalComponent.show({
      type: 'info',
      title: 'ترقية الباقة',
      subtitle: 'لترقية باقتك والاستفادة من مزايا إضافية، يرجى التواصل مع فريق خدمة العملاء. سيساعدك الفريق في اختيار الباقة المناسبة وإتمام إجراءات الترقية.',
      confirmButtonText: 'حسناً',
      cancelButtonText: 'إلغاء',
      confirmOnDismiss: false,
    });
  }

  onLogoLoadError(): void {
    this.brandingState.setLogo(null);
  }

  private loadAcademyLogo(user: MgUser | null): void {
    const academyId = this.resolveAcademyId(user);
    if (!academyId || academyId === this.loadedAcademyId) {
      return;
    }

    this.loadedAcademyId = academyId;
    this.brandingService
      .apiV1AcademyAcademyIdBrandingGet({ academy_id: academyId })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          const logoUrl = response?.data?.logo || this.combineLogoUrl(response?.data);
          this.brandingState.setLogo(logoUrl);
        },
        error: () => this.brandingState.setLogo(null)
      });
  }

  private resolveAcademyId(user: MgUser | null): string {
    const academyId = user?.academy_id ?? user?.academy?.id ?? user?.parent_id;
    return academyId ? String(academyId) : '';
  }

  private combineLogoUrl(data?: BrandingGetBrandingColorsLogoResponseData): string | undefined {
    if (!data?.logo_base_url || !data?.logo_path) {
      return undefined;
    }

    return `${data.logo_base_url}${data.logo_path}`;
  }
}
